/**
 * Covermatic Studio purchase modal controller.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;
  const { Config, Utils } = runtime;

  class PurchaseModalController {
    constructor(owner) {
      this.owner = owner;
      this.modal = Utils.$('buyModal');
      this.checkoutButton = Utils.$('buyModalCheckout');
      this.quantityInput = Utils.$('#buy-quantity');
      this.quantityMinusButton = Utils.$('#buy-quantity-minus');
      this.quantityPlusButton = Utils.$('#buy-quantity-plus');
      this.quantitySummary = Utils.$('#buy-quantity-summary');
      this.selectedPackId = '25';
      this.quantity = 1;
      this.isCheckingOut = false;

      this.init();
    }

    init() {
      if (this.checkoutButton) {
        this.checkoutButton.addEventListener('click', () => this.checkout());
      }
      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', () => this.setQuantity(this.quantityInput.value));
        this.quantityInput.addEventListener('blur', () => this.setQuantity(this.quantityInput.value));
      }
      if (this.quantityMinusButton) {
        this.quantityMinusButton.addEventListener('click', () => this.setQuantity(this.quantity - 1));
      }
      if (this.quantityPlusButton) {
        this.quantityPlusButton.addEventListener('click', () => this.setQuantity(this.quantity + 1));
      }
      this.updateQuantityUi();
    }

    handleDocumentClick(event) {
      if (event.target.closest(Utils.resolveSelector('openBuyTrigger'))) {
        event.preventDefault();
        this.openBuyModal();
        return true;
      }

      const packBtn = event.target.closest('.pack');
      if (packBtn && packBtn.closest(Utils.resolveSelector('packGrid'))) {
        this.selectPack(packBtn.dataset.pack);
        return true;
      }

      return false;
    }

    openBuyModal() {
      if (!this.modal) return;
      this.modal.classList.remove('hidden');
      this.selectPack(this.selectedPackId || '25');
      this.setQuantity(this.quantity || 1);
      Utils.syncBodyLock();
    }

    closeBuyModal() {
      if (!this.modal) return;
      this.modal.classList.add('hidden');
      Utils.syncBodyLock();
    }

    selectPack(id) {
      const nextId = id && Config.packs[id] ? id : (Object.keys(Config.packs)[0] || '25');
      this.selectedPackId = nextId;

      Utils.$$('packButtons').forEach((button) => {
        const selected = button.dataset.pack === nextId;
        button.classList.add('transition', 'duration-150', 'relative');
        button.classList.toggle('is-selected', selected);
        if (selected) {
          button.setAttribute('aria-pressed', 'true');
        } else {
          button.removeAttribute('aria-pressed');
        }
      });
      this.updateQuantityUi();
    }

    setQuantity(value) {
      const parsed = parseInt(value, 10);
      const nextQuantity = Number.isFinite(parsed) ? parsed : 1;
      this.quantity = Math.max(1, Math.min(20, nextQuantity));
      this.updateQuantityUi();
    }

    getSelectedCredits() {
      const credits = parseInt(this.selectedPackId, 10);
      return Number.isFinite(credits) ? credits : 0;
    }

    updateQuantityUi() {
      if (this.quantityInput) this.quantityInput.value = String(this.quantity);
      if (this.quantityMinusButton) this.quantityMinusButton.disabled = this.quantity <= 1;
      const totalCredits = this.getSelectedCredits() * this.quantity;
      if (this.quantitySummary) {
        this.quantitySummary.textContent = `${this.quantity} ${this.quantity === 1 ? 'pack' : 'packs'} selected${totalCredits ? ` - ${totalCredits} credits total` : ''}`;
      }
      if (this.checkoutButton && !this.isCheckingOut) {
        this.checkoutButton.textContent = this.quantity > 1 ? `Checkout - ${this.quantity} packs` : 'Checkout';
      }
    }

    getCookieValue(name) {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    }

    getStoredValue(key) {
      try {
        const localValue = window.localStorage ? window.localStorage.getItem(key) : null;
        if (localValue) return localValue;
      } catch (error) {}
      return this.getCookieValue(key);
    }

    sanitizeRef(value) {
      if (!value) return null;
      const normalized = String(value).trim();
      return /^[A-Za-z0-9_-]{1,64}$/.test(normalized) ? normalized : null;
    }

    getActiveReferral() {
      const ref = this.sanitizeRef(this.getStoredValue('referral_code'));
      const expiryRaw = this.getStoredValue('referral_expiry');
      const expiry = expiryRaw ? parseInt(expiryRaw, 10) : NaN;
      if (!ref || !Number.isFinite(expiry) || Date.now() > expiry) return null;
      return ref;
    }

    async postCartJson(url, payload = null) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...(payload ? { body: JSON.stringify(payload) } : {}),
      });
      if (!response.ok) throw new Error(`Cart request failed: ${url}`);
      return response;
    }

    async checkout() {
      const variantId = Config.packs[this.selectedPackId];
      if (!variantId) {
        Utils.toast('Pack unavailable', 'error');
        return;
      }
      if (this.isCheckingOut) return;

      try {
        this.isCheckingOut = true;
        if (this.checkoutButton) {
          this.checkoutButton.disabled = true;
          this.checkoutButton.textContent = 'Preparing checkout...';
        }
        const attributes = { return_to: Config.returnTo, source: 'Covermatic Studio' };
        const referral = this.getActiveReferral();
        if (referral) attributes.referral = referral;

        await this.postCartJson('/cart/clear.js');
        await this.postCartJson('/cart/update.js', { attributes });
        await this.postCartJson('/cart/add.js', { items: [{ id: variantId, quantity: this.quantity }] });
        window.location.href = '/checkout';
      } catch (error) {
        Utils.toast('Checkout failed', 'error');
        this.isCheckingOut = false;
        if (this.checkoutButton) {
          this.checkoutButton.disabled = false;
          this.updateQuantityUi();
        }
      }
    }
  }

  runtime.PurchaseModalController = PurchaseModalController;
})();
